import React, { FunctionComponent, useState, useEffect } from "react"
import { WellIndexTableDataI } from "../tableTypes"
import { Column, Table } from "@tanstack/react-table"
import { debounce } from "../../../util/debounce"
import Button from "../../shared/button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faChevronDoubleDown,
    faChevronDoubleUp,
} from "@fortawesome/pro-solid-svg-icons"
import styles from "./actionHeader.module.scss"
import Input from "../../shared/input"
import { useNavigate } from "react-router-dom"
import { Toggle } from "../../shared/selectionControls"
import {
    WELL_INDEX_FAVORITES_STORAGE_KEY,
    WellIndexRefetchT,
} from "../../../pages/wellIndex"
import classNames from "classnames"
import { useViewerContext } from "../../../context/ViewerContext"

interface WellIndexActionHeaderI {
    canAddWell: boolean
    globalFilter: string
    organizationColumn: Column<WellIndexTableDataI, unknown> | undefined
    favoriteColumn: Column<WellIndexTableDataI, unknown> | undefined
    table: Table<WellIndexTableDataI>
    refetchWellIndex: WellIndexRefetchT
}

const WellIndexActionHeader: FunctionComponent<WellIndexActionHeaderI> = ({
    canAddWell,
    globalFilter,
    organizationColumn,
    table,
    refetchWellIndex,
    favoriteColumn,
}) => {
    const o = organizationColumn?.getFacetedUniqueValues().keys()
    const uniqueOrganizations = o ? Array.from(o).sort() : []
    const initialBrowserWidth = window.innerWidth
    const mobileBreakpoint = 768
    const [showMobileView, setShowMobileView] = useState(
        initialBrowserWidth < mobileBreakpoint
    )

    // handlers
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value)
    }

    const handleOrganizationChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        organizationColumn?.setFilterValue(e.target.value)
    }

    // handle mobile vs desktop view
    const handleResize = () => {
        setShowMobileView(window.innerWidth < mobileBreakpoint)
    }

    useEffect(() => {
        const debouncedHandleResize = debounce(handleResize, 40)
        window.addEventListener("resize", debouncedHandleResize)
        return () => {
            window.removeEventListener("resize", debouncedHandleResize)
        }
    }, [])

    return (
        <Component
            canAddWell={canAddWell}
            globalFilter={globalFilter}
            uniqueOrganizations={uniqueOrganizations}
            organizationFilterValue={organizationColumn?.getFilterValue() || ""}
            handleSearchChange={handleSearchChange}
            handleOrganizationChange={handleOrganizationChange}
            showMobileView={showMobileView}
            refetchWellIndex={refetchWellIndex}
            favoriteColumn={favoriteColumn}
        />
    )
}

export default WellIndexActionHeader

interface ComponentI {
    canAddWell: boolean
    globalFilter: string
    uniqueOrganizations: string[]
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleOrganizationChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    showMobileView: boolean
    organizationFilterValue: unknown
    refetchWellIndex: WellIndexRefetchT
    favoriteColumn: Column<WellIndexTableDataI, unknown> | undefined
}

const Component: FunctionComponent<ComponentI> = ({
    canAddWell,
    globalFilter,
    showMobileView,
    uniqueOrganizations,
    handleOrganizationChange,
    handleSearchChange,
    organizationFilterValue,
    refetchWellIndex,
    favoriteColumn,
}) => {
    const navigate = useNavigate()
    const [showActions, setShowActions] = useState(true)
    const fontAwesomeIcon = showActions
        ? faChevronDoubleUp
        : faChevronDoubleDown

    const outerClass =
        !showMobileView || showActions ? styles.outer : styles.outerClosed
    const showOrganizationFilter = uniqueOrganizations.length > 1

    useEffect(() => {
        // Reset showActions when switching between mobile and desktop views
        if (!showMobileView) {
            setShowActions(true)
        }
    }, [showMobileView])

    return (
        <div className={outerClass}>
            {showMobileView && (
                <Button
                    handleButtonClick={() => setShowActions(!showActions)}
                    status="primary"
                >
                    <FontAwesomeIcon icon={fontAwesomeIcon} color="#fff" />
                </Button>
            )}
            {showActions && (
                <div className={styles.container}>
                    <div className={styles.filtersContainer}>
                        {canAddWell && (
                            <Button
                                handleButtonClick={() => {
                                    navigate("addWells")
                                }}
                                status="primary"
                                condition="default"
                            >
                                Add Well
                            </Button>
                        )}
                        <ToggleFavorites
                            favoriteColumn={favoriteColumn}
                            refetchWellIndex={refetchWellIndex}
                        />
                        {showOrganizationFilter && (
                            <Input
                                onChange={handleOrganizationChange}
                                type="datalist"
                                orientation="horizontal"
                                placeHolder="Filter Organizations"
                                datalistId="organizations"
                                options={uniqueOrganizations}
                                specializedClass="noMessage"
                                inputCustomClass={styles.organizationFilter}
                                value={organizationFilterValue as string}
                            />
                        )}
                    </div>
                    <Input
                        value={globalFilter || ""}
                        onChange={handleSearchChange}
                        type="text"
                        orientation="horizontal"
                        placeHolder="Search Columns"
                        specializedClass="noMessage"
                        inputCustomClass={styles.searchFilter}
                    />
                </div>
            )}
        </div>
    )
}

/**
 * ToggleFavorites component allows users to toggle between viewing all wells or only their favorite wells.
 *
 * - Current Behavior:
 * - Initializes the toggle state from localStorage.
 * - On toggle, updates localStorage and triggers `refetchWellIndex()` with the updated filter.
 *
 * - Cross-Tab Sync (Consider for future):
 * - localStorage updates can be detected across tabs via the `storage` event.
 * - Adding a `window.addEventListener("storage", ...)` listener would allow the component to update its internal `showFavorites` state when the toggle is changed in another tab.
 * - For full consistency, a `refetchWellIndex()` call should be triggered when the event fires.
 *
 * - Debouncing (Optimization Consideration):
 * - In the current implementation, `refetchWellIndex()` fires immediately on toggle.
 * - If rapid toggles become common (e.g. from UI flapping or accidental clicks), consider a debounced version of `refetchWellIndex()` to reduce network load.
 * - Keep in mind: debouncing would delay the data refresh, which might harm perceived responsiveness.
 *
 */
const ToggleFavorites: FunctionComponent<{
    refetchWellIndex: WellIndexRefetchT
    favoriteColumn: Column<WellIndexTableDataI, unknown> | undefined
}> = ({ refetchWellIndex, favoriteColumn }) => {
    // context
    const { userId } = useViewerContext().getViewer()

    // state
    const [showFavorites, setShowFavorites] = useState(() => {
        const key = WELL_INDEX_FAVORITES_STORAGE_KEY(userId)
        return localStorage.getItem(key) === "true"
    })

    // handlers
    const toggleFavorites = () => {
        const shouldShowFavorites = !showFavorites
        setShowFavorites(shouldShowFavorites)

        const key = WELL_INDEX_FAVORITES_STORAGE_KEY(userId)
        localStorage.setItem(key, String(shouldShowFavorites))

        if (shouldShowFavorites) {
            // Filter locally using the column filter
            favoriteColumn?.setFilterValue(true)
        } else {
            favoriteColumn?.setFilterValue(false)
            // Refetch from server with favorites filter turned off
            refetchWellIndex({
                filter: { favorites: false },
            })
        }
    }

    return (
        <div className={styles.toggleContainer}>
            <span
                className={classNames(styles.label, {
                    [styles.labelActive]: showFavorites,
                    [styles.labelInactive]: !showFavorites,
                })}
            >
                Favorites Only:
            </span>
            <div>
                <Toggle
                    checked={showFavorites}
                    handleToggleClick={toggleFavorites}
                    toggleLabel=""
                />
            </div>
        </div>
    )
}
